import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white flex-col justify-between p-12">
        <div>
          <div className="flex items-center gap-4">
            <img 
              src="/mypropmate-logo.png" 
              alt="MyPropMate Logo" 
              className="w-16 h-16 object-contain"
            />
            <div>
              <h1 className="text-2xl font-heading font-bold tracking-tight">MYPROPMATE</h1>
              <p className="text-sm text-slate-400 uppercase tracking-widest">Smart Property Solutions</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          <h2 className="text-4xl font-heading font-bold leading-tight">
            Quality Inspection<br />
            <span className="text-cyan-400">Made Simple</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-md">
            Professional property inspection reports with comprehensive documentation and seamless collaboration.
          </p>
        </div>
        
        <div className="space-y-1">
          <p className="text-slate-400 text-sm font-medium">MYONK INFRASOLUTIONS LLP</p>
          <p className="text-slate-500 text-xs">
            &copy; 2024 MyPropMate. All rights reserved.
          </p>
        </div>
      </div>
      
      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center gap-3 mb-8">
            <img 
              src="/mypropmate-logo.png" 
              alt="MyPropMate Logo" 
              className="w-20 h-20 object-contain"
            />
            <div className="text-center">
              <h1 className="text-xl font-heading font-bold text-slate-900">MYPROPMATE</h1>
              <p className="text-xs text-slate-500">MYONK INFRASOLUTIONS LLP</p>
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-sm p-8">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Sign In</h2>
            <p className="text-slate-500 mb-8">Enter your credentials to access your account</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-sm border-slate-200 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 rounded-sm border-slate-200 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-semibold transition-all active:scale-[0.98]"
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner w-5 h-5"></span>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Sign In
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                Contact your administrator for account access
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
